import { api } from '@agency/shared';
import { Callout, Card, KeyValue, PageHeader, Skeleton, fmtMicroUsd, fmtTimestamp } from '@agency/ui';
import { useEffect, useMemo, useState } from 'react';
import { ErrorCallout, Grid, JsonEditor, Page, SensitiveButton, UserLink } from '../../app/widgets.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

export function ModelConfigPage() {
  const session = useSession();
  const mutation = useMutation();
  const q = useApi('admin.ops.modelConfig.get');
  const mc = q.data;
  const canPublish = session.has('template.publish');
  const editable = (m: api.adminOps.ModelConfig) => ({ defaultModel: m.defaultModel, higherCapabilityModel: m.higherCapabilityModel, permittedModels: m.permittedModels, maxOutputTokens: m.maxOutputTokens, reasoningEffort: m.reasoningEffort, temperature: m.temperature, priceTable: m.priceTable, perUserBudgetMicroUsdPerMonth: m.perUserBudgetMicroUsdPerMonth, maxReservationMicroUsd: m.maxReservationMicroUsd, baseUrl: m.baseUrl, policyVersion: m.policyVersion });
  const [text, setText] = useState('');
  useEffect(() => {
    if (mc) setText(JSON.stringify(editable(mc), null, 2));
  }, [mc]);
  const parsed = useMemo(() => {
    try {
      const v = api.adminOps.ModelConfigUpdateRequestSchema.omit({ revision: true }).safeParse(JSON.parse(text));
      return v.success ? { value: v.data, error: null } : { value: null, error: v.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') };
    } catch (e) {
      return { value: null, error: e instanceof Error ? e.message : 'invalid JSON' };
    }
  }, [text]);
  const dirty = mc ? text !== JSON.stringify(editable(mc), null, 2) : false;
  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Model config"
        title="Model configuration"
        subtitle={mc ? `Version ${mc.version} · default ${mc.defaultModel} · higher ${mc.higherCapabilityModel ?? '--'} · max output ${mc.maxOutputTokens.toLocaleString('en-US')} tokens · price table ${mc.priceTable.version} (${fmtTimestamp(mc.priceTable.effectiveFrom)}) · updated ${fmtTimestamp(mc.updatedAt)}` : 'Loading model configuration.'}
        actions={canPublish ? <SensitiveButton variant="primary" disabled={!dirty || !parsed.value} loading={mutation.busy} onClick={async () => { if (!mc || !parsed.value) return; await mutation.run('admin.ops.modelConfig.set', { input: { ...parsed.value, revision: mc.revision } }, { title: (d) => `Model config published · v${d.version}`, invalidate: ['admin.ops.modelConfig'] }); }}>Publish version</SensitiveButton> : undefined}
      />
      <ErrorCallout error={q.error} onRetry={() => void q.reload()} className="mt-6" />
      {!mc ? (
        <Skeleton height={320} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Active configuration" subtitle={`policy ${mc.policyVersion}`} span="col-span-12 lg:col-span-5">
            <KeyValue
              items={[
                { key: 'Default model', value: mc.defaultModel, mono: true },
                { key: 'Higher-capability model', value: mc.higherCapabilityModel ?? '--', mono: true },
                { key: 'Permitted models', value: mc.permittedModels.join(', '), mono: true },
                { key: 'Max output tokens', value: mc.maxOutputTokens.toLocaleString('en-US'), mono: true },
                { key: 'Reasoning effort', value: mc.reasoningEffort ?? '--', mono: true },
                { key: 'Temperature', value: mc.temperature === null ? '--' : String(mc.temperature), mono: true },
                { key: 'Per-user budget / month', value: mc.perUserBudgetMicroUsdPerMonth === null ? 'unlimited' : fmtMicroUsd(mc.perUserBudgetMicroUsdPerMonth), mono: true },
                { key: 'Max reservation', value: fmtMicroUsd(mc.maxReservationMicroUsd), mono: true },
                { key: 'Base URL', value: mc.baseUrl, mono: true },
                { key: 'Updated by', value: <UserLink userId={mc.updatedByUserId} /> },
                { key: 'Revision', value: String(mc.revision), mono: true },
              ]}
            />
            <p className="mt-3 text-xs text-mute">In local mode the runtime base URL comes from the assigned key's grant, not from this configuration.</p>
            <div className="mt-5 border-t border-hair pt-4">
              <p className="label-mono">Price table {mc.priceTable.version} · effective {fmtTimestamp(mc.priceTable.effectiveFrom)} · micro-USD per 1M tokens</p>
              <table className="mt-2 w-full text-[12.5px]">
                <thead><tr className="label-mono border-b border-hair text-left"><th className="py-1.5 font-normal">Model</th><th className="py-1.5 text-right font-normal">Input</th><th className="py-1.5 text-right font-normal">Cached</th><th className="py-1.5 text-right font-normal">Output</th></tr></thead>
                <tbody>
                  {Object.entries(mc.priceTable.models).map(([model, p]) => (
                    <tr key={model} className="border-b border-hair">
                      <td className="py-1.5 font-mono text-xs-plus">{model}</td>
                      <td className="py-1.5 text-right font-mono tabular-nums">{fmtMicroUsd(p.inputMicroUsdPerMillion)}</td>
                      <td className="py-1.5 text-right font-mono tabular-nums">{fmtMicroUsd(p.cachedInputMicroUsdPerMillion)}</td>
                      <td className="py-1.5 text-right font-mono tabular-nums">{fmtMicroUsd(p.outputMicroUsdPerMillion)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card title="Edit" subtitle="Publishing creates a new content version" span="col-span-12 lg:col-span-7">
            {!canPublish ? <Callout tone="info" className="mb-4">Publishing requires template.publish and recent authentication.</Callout> : null}
            <JsonEditor value={text} onChange={setText} rows={26} label="Model configuration (JSON)" error={parsed.error} />
          </Card>
        </Grid>
      )}
    </Page>
  );
}
